<template>
  <div class="space-y-6">
    <!-- Loading state -->
    <div v-if="isFetching" class="flex items-center justify-center gap-3 py-12">
      <i class="pi pi-spin pi-spinner text-navy-400 text-lg" />
      <span class="text-navy-500 text-sm">
        Fetching documents from {{ agent.name || "agent" }}...
      </span>
    </div>

    <!-- Error state -->
    <AppCard v-else-if="fetchError" title="Error" accent="bg-red-500">
      <p class="text-sm text-red-600">{{ fetchError }}</p>
      <template #footer>
        <AppButton variant="secondary" @click="fetchDocuments">
          Retry
        </AppButton>
      </template>
    </AppCard>

    <!-- Document selection -->
    <AppCard
      v-else-if="fetchedDocuments && fetchedDocuments.length > 0"
      title="Select Documents"
      subtitle="Choose which documents to include in the order"
    >
      <DocumentTree
        :documents="fetchedDocuments"
        :selected-ids="selectedDocumentIds"
        @toggle-document="$emit('toggleDocument', $event)"
        @toggle-folder="$emit('toggleFolder', $event)"
        @select-all="$emit('selectAll')"
        @deselect-all="$emit('deselectAll')"
      />
    </AppCard>

    <!-- Empty state: no documents -->
    <AppCard
      v-else-if="fetchedDocuments && fetchedDocuments.length === 0"
      title="No Documents Found"
    >
      <p class="text-navy-500 text-sm">
        No documents were found for this file number. You can add documents
        after the order is created.
      </p>
    </AppCard>

    <!-- Navigation footer -->
    <div v-if="!isFetching" class="flex items-center justify-between">
      <button
        class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
        @click="$emit('back')"
      >
        &larr; Back to parties
      </button>
      <div class="flex items-center gap-3">
        <span
          v-if="fetchedDocuments && fetchedDocuments.length > 0"
          class="text-navy-400 text-sm"
        >
          {{ selectedDocumentIds.length }} document{{
            selectedDocumentIds.length === 1 ? "" : "s"
          }}
          selected
        </span>
        <AppButton @click="$emit('continue')"> Continue to Review </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue"

import type {
  ConnectedAgent,
  FetchedDocument,
  FetchedDocumentsResponse,
} from "../../pages/Order/types"
import DISPATCH_FETCH_DOCUMENTS from "../../queries/agent_registration/dispatchFetchDocuments.gql"
import POLL_WORK_ITEM from "../../queries/agent_work_item/poll.gql"
import { apolloClient } from "../../queries/client"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"
import DocumentTree from "./DocumentTree.vue"

const props = defineProps<{
  agent: ConnectedAgent
  fileNumber: string
  fetchedDocuments: FetchedDocument[] | null
  selectedDocumentIds: string[]
}>()

const emit = defineEmits<{
  documentsFetched: [docs: FetchedDocument[]]
  toggleDocument: [documentId: string]
  toggleFolder: [folderPath: string]
  selectAll: []
  deselectAll: []
  back: []
  continue: []
}>()

const isFetching = ref(false)
const fetchError = ref<string | undefined>()
let pollInterval: ReturnType<typeof setInterval> | null = null

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})

onMounted(() => {
  if (!props.fetchedDocuments) {
    fetchDocuments()
  }
})

const fetchDocuments = async () => {
  isFetching.value = true
  fetchError.value = undefined

  try {
    const result = await apolloClient.mutate({
      mutation: DISPATCH_FETCH_DOCUMENTS,
      variables: {
        agentRegistrationId: props.agent.id,
        fileNumber: props.fileNumber,
      },
    })

    if (result?.data?.dispatchFetchDocuments?.errors?.length) {
      fetchError.value = result.data.dispatchFetchDocuments.errors.join(", ")
      isFetching.value = false
      return
    }

    const workItemId = result.data.dispatchFetchDocuments.workItem.id
    pollForResult(workItemId)
  } catch (error) {
    console.error("Dispatch error:", error)
    fetchError.value = "Failed to dispatch document fetch request"
    isFetching.value = false
  }
}

const pollOnce = async (workItemId: string) => {
  try {
    const result = await apolloClient.query({
      query: POLL_WORK_ITEM,
      variables: { id: workItemId },
      fetchPolicy: "network-only",
    })

    const item = result.data.agentWorkItem
    if (!item) return

    if (item.status === "completed") {
      clearInterval(pollInterval!)
      pollInterval = null
      isFetching.value = false
      const response = item.result as FetchedDocumentsResponse
      emit("documentsFetched", response.documents)
    } else if (item.status === "failed") {
      clearInterval(pollInterval!)
      pollInterval = null
      isFetching.value = false
      fetchError.value = item.errorMessage || "Failed to fetch documents"
    }
  } catch (error) {
    console.error("Poll error:", error)
    clearInterval(pollInterval!)
    pollInterval = null
    isFetching.value = false
    fetchError.value = "Lost connection while fetching documents"
  }
}

const pollForResult = (workItemId: string) => {
  if (pollInterval) clearInterval(pollInterval)

  pollInterval = setInterval(() => {
    void pollOnce(workItemId)
  }, 2000)
}
</script>
